import { Alert, Button, Icon, IconName, TextInput } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PolicyUserInfo } from '..';
import { Container } from '../../../components/ui/Container';
import { Logo } from '../../../components/ui/Logo';
import { useContent } from '../../../hooks/useContent';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { useInput } from '../../../hooks/useInput';
import { usePasswordInput } from '../../../hooks/usePasswordInput';
import { useEvent } from '../../../utils/analytics/useEvent';
import { MaxLengths } from '../../../utils/constants';
import {
  StyledPasswordHint,
  StyledAlertContainer,
  StyledHeading,
  StyledLink,
  StyledNewPasswordContainer,
  StyledConfirmPasswordContainer,
  StyledPasswordHintList,
  StyledPasswordHintItem,
  StyledPasswordHintText,
} from '../styles';

export function ResetPasswordForm({
  isLoading,
  resetPassword,
  error,
}: {
  isLoading: boolean;
  resetPassword: (nextPassword: string) => void;
  error: string | null;
  userInfo?: PolicyUserInfo;
}) {
  const trackEvent = useEvent();
  const { validateField, invalidFields } = useFieldValidation();
  const { getContentByPath } = useContent();
  const [trackedPassword, setTrackedPassword] = useState('');
  const [trackedConfirmPassword, setTrackedConfirmPassword] = useState('');

  const {
    value: password,
    bind: bindPasswordField,
    isValid: isValidPassword,
    validations: passwordValidations,
  } = usePasswordInput('', 'password', validateField);

  const { value: passwordConfirm, bind: bindPasswordConfirmField } = useInput(
    '',
    'passwordConfirm',
    undefined,
    validateField,
  );

  const hasPasswordValidationError = invalidFields.includes('password') || !isValidPassword;
  const hasPasswordConfirmValidationError =
    invalidFields.includes('passwordConfirm') || (password && password !== passwordConfirm);

  const canProcessRequest = useMemo(
    () =>
      !!(
        isValidPassword &&
        password &&
        passwordConfirm &&
        password === passwordConfirm &&
        !isLoading &&
        !invalidFields.length
      ),
    [invalidFields.length, isLoading, isValidPassword, password, passwordConfirm],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      const className = !password ? '' : isValid ? 'hint-success' : 'hint-error';
      const iconName: IconName = !password || isValid ? 'checkmark-circle' : 'symbol-x-circle';

      const requirementText = getContentByPath(
        !password
          ? 'shared.fields.requirement'
          : isValid
          ? 'shared.fields.requirementMet'
          : 'shared.fields.requirementUnmet',
      );
      if (!password) {
        return (
          <StyledPasswordHintItem>
            <StyledPasswordHintText
              data-testid={`password-hint-${validation}`}
              className={className}
              variant='body-regular-s'
              as='p'
            >
              {hint}
            </StyledPasswordHintText>
          </StyledPasswordHintItem>
        );
      }
      return (
        <StyledPasswordHint
          data-testid={`password-hint-${validation}`}
          className={className}
          variant='body-regular-s'
          as='p'
        >
          <Icon
            name={iconName}
            size='small'
            role='img'
            aria-hidden={false}
            focusable
            aria-label={requirementText as string}
          />
          {hint}
        </StyledPasswordHint>
      );
    },
    [getContentByPath, password, passwordValidations],
  );

  useEffect(() => {
    const isInvalidPassword = invalidFields.includes('password');

    if (isInvalidPassword) {
      const errorPath = isInvalidPassword
        ? 'resetPassword.steps.reset.fields.password.validation.empty'
        : 'resetPassword.steps.reset.fields.password.validation.passwordPolicy';

      const errorMessage = getContentByPath(errorPath) as string;
      if (trackedPassword !== errorMessage) {
        trackEvent(
          'identity – reset password - credentials input error',
          'reset password credentials input error',
          'resetpinputerror',
          {
            errorField: 'New Password',
            errorDisplayed: errorMessage,
          },
        );
        setTrackedPassword(errorMessage);
      }
    } else {
      setTrackedPassword('');
    }

    const isPasswordEmpty = !password.length && !passwordConfirm.length;
    const isPasswordMismatch = password.length > 0 && passwordConfirm.length > 0 && password !== passwordConfirm;
    const isInvalidConfirmPassword = invalidFields.includes('passwordConfirm');

    if (isPasswordEmpty) {
      return;
    } else if (isInvalidConfirmPassword || isPasswordMismatch) {
      const errorPath = isInvalidConfirmPassword
        ? 'resetPassword.steps.reset.fields.passwordConfirm.validation.empty'
        : 'resetPassword.steps.reset.fields.passwordConfirm.validation.mismatch';

      const errorMessage = getContentByPath(errorPath) as string;
      if (trackedConfirmPassword !== errorMessage) {
        trackEvent(
          'identity – reset password - credentials input error',
          'reset password credentials input error',
          'resetpinputerror',
          {
            errorField: 'Re-type Password',
            errorDisplayed: errorMessage,
          },
        );
        setTrackedConfirmPassword(errorMessage);
      }
    } else {
      setTrackedConfirmPassword('');
    }
  }, [
    getContentByPath,
    invalidFields,
    isValidPassword,
    password,
    passwordConfirm,
    trackEvent,
    trackedConfirmPassword,
    trackedPassword,
  ]);

  const getPasswordFieldError = useCallback(() => {
    if (invalidFields.includes('password')) {
      return getContentByPath('resetPassword.steps.reset.fields.password.validation.empty');
    }

    if (!isValidPassword) {
      return getContentByPath('resetPassword.steps.reset.fields.password.validation.passwordPolicy');
    }

    return '';
  }, [invalidFields, isValidPassword, getContentByPath]);

  const getPasswordConfirmationFieldError = useCallback(() => {
    if (!password.length && !passwordConfirm.length) {
      return;
    }

    if (invalidFields.includes('passwordConfirm')) {
      return getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.validation.empty');
    }

    if (password.length > 0 && passwordConfirm.length > 0 && password !== passwordConfirm) {
      return getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.validation.mismatch');
    }

    return '';
  }, [getContentByPath, invalidFields, password, passwordConfirm]);

  const passwordError = useMemo(
    () => (invalidFields.includes('password') || (password && !isValidPassword) ? getPasswordFieldError() : undefined),
    [getPasswordFieldError, invalidFields, isValidPassword, password],
  );

  const passwordConfirmError = useMemo(
    () =>
      invalidFields.includes('passwordConfirm') || passwordConfirm ? getPasswordConfirmationFieldError() : undefined,
    [getPasswordConfirmationFieldError, invalidFields, passwordConfirm],
  );
  const renderPasswordHints = () => (
    <>
      {passwordValidationHint('length', getContentByPath('changePassword.fields.newPassword.validation.lengthHint'))}
      {passwordValidationHint('number', getContentByPath('changePassword.fields.newPassword.validation.numberHint'))}
      {passwordValidationHint(
        'uppercase',
        getContentByPath('changePassword.fields.newPassword.validation.uppercaseHint'),
      )}
      {passwordValidationHint(
        'lowercase',
        getContentByPath('changePassword.fields.newPassword.validation.lowercaseHint'),
      )}
      {passwordValidationHint(
        'specialCharacter',
        getContentByPath('changePassword.fields.newPassword.validation.specialCharacterHint'),
      )}
    </>
  );
  return (
    <Container height='auto'>
      <Logo />
      <StyledHeading variant='heading-medium-3' as='h1' className='identity__page-heading' testId='page-title'>
        {getContentByPath('resetPassword.steps.reset.title')}
      </StyledHeading>
      {error && (
        <StyledAlertContainer>
          <Alert open variant='error' message={error} testId='reset-password-error' hiddenCloseButton />
        </StyledAlertContainer>
      )}
      {!isValidPassword && (
        <div className='identity__password-policy'>
          <div className='identity__password-hits-wrapper' data-testid='hints-wrapper'>
            {!password ? (
              <StyledPasswordHintList>{renderPasswordHints()}</StyledPasswordHintList>
            ) : (
              <>{renderPasswordHints()}</>
            )}
          </div>
        </div>
      )}
      {password && isValidPassword && (
        <div>
          <StyledPasswordHint variant='body-regular-s' className='hint-success' testId='reset-password-all-met'>
            <Icon
              name='checkmark-circle'
              size='small'
              role='img'
              aria-hidden='false'
              focusable
              aria-label={getContentByPath('resetPassword.steps.reset.fields.password.validation.allMet')}
            />
            {getContentByPath('resetPassword.steps.reset.fields.password.validation.allMet')}
          </StyledPasswordHint>
        </div>
      )}
      <StyledNewPasswordContainer>
        <TextInput
          variant='password'
          id='password'
          testId='password'
          label={getContentByPath('resetPassword.steps.reset.fields.password.label')}
          disabled={isLoading}
          className={`identity__field password ${hasPasswordValidationError ? 'with-error' : ''}`}
          error={!!passwordError}
          hint={passwordError as string}
          {...bindPasswordField}
          maxLength={MaxLengths.Password}
          autoComplete='new-password'
        />
      </StyledNewPasswordContainer>
      <StyledConfirmPasswordContainer>
        <TextInput
          variant='password'
          id='passwordConfirm'
          testId='passwordConfirm'
          label={getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.label')}
          disabled={isLoading}
          className={`identity__field password-confirm ${hasPasswordConfirmValidationError ? 'with-error' : ''}`}
          error={!!passwordConfirmError}
          hint={passwordConfirmError as string}
          {...bindPasswordConfirmField}
          maxLength={MaxLengths.Password}
          autoComplete='new-password'
        />
      </StyledConfirmPasswordContainer>
      <Button
        label={getContentByPath('resetPassword.steps.reset.buttons.primaryAction.label')}
        size='large'
        variant='primary'
        fullWidth
        onClick={() => resetPassword(password)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            resetPassword(password);
          }
        }}
        className='identity__form-submit'
        disabled={!canProcessRequest}
        loading={false}
        type='submit'
      />

      <StyledLink
        className='identity__secondary-action'
        label={getContentByPath('resetPassword.steps.reset.buttons.secondaryAction.label')}
        size='large'
        variant='tertiary'
        icon='arrow-left'
        fullWidth
        onClick={async () => {
          await trackEvent(
            'identity – reset password - back to sign in',
            'back to sign in',
            'resetpbacktosignin',
            {},
            true,
          );
          navigate('/');
        }}
        onKeyDown={async e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            await trackEvent(
              'identity – reset password - back to sign in',
              'back to sign in',
              'resetpbacktosignin',
              {},
              true,
            );
            navigate('/');
          }
        }}
      />
    </Container>
  );
}

ResetPasswordForm.displayName = 'ResetPasswordForm';
